import { later } from '@ember/runloop';
import EmberService, { service } from '@ember/service';

import moment from 'moment';

import type { APIData, Service } from 'webapp-types';
import { Direction } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { reload } from 'webapp/utils/common';

import CommonPopupsReloadContextComponent from 'webapp/components/common/popups/reload-context';

import type { RSjqXHR } from 'webapp/services/request';

export enum AppType {
	GENERAL = 'app',
	DATAWALL = 'dw',
}

type triggerAfterOtherPopupsClosed = () => void;

export default class VersionCheckerService extends EmberService {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;

	version: string | null = null; // current version
	appType: AppType | null = null; // 'app' for general webapp and 'dw' on public datawalls
	isEnabled = true;

	private checkInterval = 600; //  10 min in seconds
	private infoTriggered = false;
	private reloadTriggered = false;
	private nextQueryTimestamp = 0; // approximate, just so we can assert query or not query on visibilitychange & online events
	private queryVersionLoadingXHR: RSjqXHR | null = null;

	bindEvents() {
		this.scheduleNextQuery();

		document.addEventListener(
			'visibilitychange',
			() => {
				if (!document.hidden) {
					// just became visible
					this.queryIfLongTimeWithoutRequest();
				}
			},
			false,
		);

		window.addEventListener('online', () => {
			// restored connection
			this.queryIfLongTimeWithoutRequest();
		});
	}

	private scheduleNextQuery(): void {
		this.nextQueryTimestamp = moment().unix() + this.checkInterval;
		later(this, this.queryVersion, this.checkInterval * 1000);
	}

	private queryVersion(): void {
		if (!this.isEnabled) {
			return;
		}

		if (window.env === 'local') {
			return;
		}

		if (this.navigationService.isLoadedFromSnapshooter) {
			return;
		}

		if (!this.version || !this.appType) {
			return;
		}

		if (this.reloadTriggered) {
			return;
		}

		if (document.hidden) {
			return;
		}

		if (!window.navigator.onLine) {
			return;
		}

		// if request already in progress, cancel it
		if (this.queryVersionLoadingXHR && this.queryVersionLoadingXHR.readyState !== 4) {
			this.queryVersionLoadingXHR.abort();
		}

		const data = {
			type: this.appType,
			version: this.version,
		};

		const promise = this.requestService.app<APIData.Version.CheckResponse>(
			'POST',
			'/version',
			data,
		);

		this.queryVersionLoadingXHR = promise.jqPromise as RSjqXHR;

		promise
			.then((reply) => {
				if (!reply) {
					return;
				}
				const changesText = (reply.changes || []).join('<br>');
				if (reply.isReload && !this.reloadTriggered) {
					// show reload and dont query versions anymore
					this.reloadTriggered = true;
					this.showChangeReload(changesText);
				} else if (reply.isInfo && !this.infoTriggered) {
					// show info, but keep querying in case reload will come
					this.infoTriggered = true;
					this.showChangeInfo(changesText);
				}
			})
			.finally(() => {
				this.scheduleNextQuery();
			})
			.catch(() => {
				// don't log any errors
			});
	}

	private triggerAfterOtherPopupsClosed(fn: triggerAfterOtherPopupsClosed): void {
		if (this.popupService.anyPopupsOpen) {
			this.eventManagerService.on(AppEvent.ALL_POPUPS_CLOSED, this, () => {
				fn();
				return true; // just once, unbind after
			});
		} else {
			fn();
		}
	}

	private queryIfLongTimeWithoutRequest(): void {
		if (this.nextQueryTimestamp < moment().unix()) {
			this.queryVersion();
		}
	}

	private showChangeInfo(changesText: string): void {
		this.triggerAfterOtherPopupsClosed(() => {
			this.analyticsService.event(TrackingEvent.SHOW_UPDATE_INFOLAYER);

			const attachTo = document.body; // not so important - has fixed position in css
			this.popupService
				.createContext(CommonPopupsReloadContextComponent, attachTo, {
					popupClass: 'popup-type-reload-context',
					directions: [Direction.RIGHT],
					offset: 66,
					hideArrow: true,
					theme: 'blue',
				})
				.open({ changesText });
		});
	}

	private showChangeReload(changesText: string): void {
		if (this.appType === AppType.DATAWALL) {
			reload();
		} else {
			this.triggerAfterOtherPopupsClosed(() => {
				this.analyticsService.event(TrackingEvent.SHOW_UPDATE_TAKEOVER);
				this.navigationService.openReloadModal({ changesText, fromVersionChecker: true });
			});
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		'version-checker': VersionCheckerService;
	}
}
